import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar, Box, ButtonBase, Divider, Drawer, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { NotificationsMenu } from './NotificationsMenu';
import { TaskTimerIndicator } from './TaskTimerIndicator';
import { SidebarNav, Monogram } from './navigation/SidebarNav';
import { CommandPalette } from './navigation/CommandPalette';
import { ROLE_LABELS } from './navigation/navConfig';
import { useAuthStore, UserRole } from '../store/authStore';
import { useSalonContextStore } from '../store/salonContextStore';
import { tokens } from '../theme';
import { initials } from '../utils/format';

const RAIL_WIDTH = 76;
const DRAWER_WIDTH = 272;
const COLLAPSE_KEY = 'visitio.sidebar.collapsed';

// Role, ktore maja dostep do strony „Konto” (/salon/account) — kopia z App.tsx.
const ACCOUNT_ROLES: UserRole[] = ['SALON_OWNER', 'STAFF', 'MANAGER', 'ACCOUNTANT', 'ANALYST', 'MARKETER', 'IT'];

// Strony, ktore potrzebuja calej szerokosci (bez limitu 1400 px).
const FULL_WIDTH_PATHS = ['/salon/calendar', '/time/calendar'];

const readCollapsed = () => {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
};

export const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuthStore();
  const { loadSalons } = useSalonContextStore();

  useEffect(() => {
    if (user && (user.role === 'SALON_OWNER' || user.role === 'STAFF')) {
      loadSalons();
    }
  }, [user, loadSalons]);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* tryb prywatny */ }
  }, [collapsed]);

  // Ctrl+K / Cmd+K otwiera wyszukiwarke.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const railCollapsed = !isMobile && collapsed;
  const sidebarWidth = railCollapsed ? RAIL_WIDTH : DRAWER_WIDTH;
  const isFullWidthPage = FULL_WIDTH_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const displayName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.email || '';

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, maxWidth: '86vw', border: 'none', borderRadius: 0, bgcolor: tokens.aubergine } }}
        >
          <SidebarNav collapsed={false} onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            transition: 'width .2s ease',
            '& .MuiDrawer-paper': {
              width: sidebarWidth, border: 'none', borderRadius: 0, overflowX: 'hidden', bgcolor: tokens.aubergine,
              transition: 'width .2s ease',
            },
          }}
        >
          <SidebarNav collapsed={railCollapsed} />
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Gorny pasek */}
        <Box
          component="header"
          sx={{
            position: 'sticky', top: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1.5 },
            px: { xs: 1.5, md: 3 }, minHeight: { xs: 58, md: 64 },
            bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'saturate(1.4) blur(10px)',
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          {isMobile ? (
            <>
              <IconButton onClick={() => setMobileOpen(true)} aria-label="Otwórz menu" sx={{ color: tokens.ink }}>
                <MenuRoundedIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={() => navigate('/dashboard')} role="link" aria-label="Panel">
                <Monogram size={30} />
              </Box>
            </>
          ) : (
            <Tooltip title={collapsed ? 'Rozwiń menu' : 'Zwiń menu'}>
              <IconButton onClick={() => setCollapsed((v) => !v)} aria-label={collapsed ? 'Rozwiń menu' : 'Zwiń menu'}>
                {collapsed ? <KeyboardDoubleArrowRightRoundedIcon /> : <KeyboardDoubleArrowLeftRoundedIcon />}
              </IconButton>
            </Tooltip>
          )}

          {!isMobile && (
            <ButtonBase
              onClick={() => setPaletteOpen(true)}
              aria-label="Szukaj"
              sx={{
                width: { md: 320, lg: 420 }, height: 42, px: 1.5, gap: 1.25, justifyContent: 'flex-start', borderRadius: '11px',
                bgcolor: tokens.surfaceStrong, color: tokens.muted, fontFamily: 'inherit', fontSize: '0.90625rem',
                border: '1px solid transparent',
                '&:hover': { borderColor: tokens.lineStrong, bgcolor: '#EEEBF3' },
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 20 }} />
              <Box component="span" sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {user && ['SALON_OWNER', 'STAFF', 'MARKETER'].includes(user.role) ? 'Szukaj klienta lub strony' : 'Szukaj strony'}
              </Box>
              <Box component="kbd" sx={{ fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 700, color: tokens.muted, bgcolor: '#fff', border: `1px solid ${tokens.line}`, borderRadius: '6px', px: 0.75, lineHeight: '20px' }}>
                {isMac ? '⌘ K' : 'Ctrl K'}
              </Box>
            </ButtonBase>
          )}

          <Box sx={{ flex: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 0.75 } }}>
            <TaskTimerIndicator />
            {isMobile && (
              <IconButton onClick={() => setPaletteOpen(true)} aria-label="Szukaj">
                <SearchRoundedIcon />
              </IconButton>
            )}
            <NotificationsMenu />
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Menu konta" aria-haspopup="menu" sx={{ p: 0.5, ml: 0.25 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: tokens.plum, color: '#fff', fontSize: '0.8125rem' }}>
                {initials(user?.firstName, user?.lastName, user?.email)}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              slotProps={{ paper: { sx: { mt: 1, minWidth: 250 } } }}
            >
              <Box sx={{ px: 1.5, pt: 1, pb: 1.25 }}>
                <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>{displayName}</Typography>
                {user?.firstName && <Typography noWrap sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{user.email}</Typography>}
                {user && <Typography sx={{ fontSize: '0.8125rem', color: tokens.champagneDeep, fontWeight: 600, mt: 0.25 }}>{ROLE_LABELS[user.role]}</Typography>}
              </Box>
              <Divider sx={{ my: 0.5 }} />
              {user && ACCOUNT_ROLES.includes(user.role) && (
                <MenuItem onClick={() => { setAnchorEl(null); navigate('/salon/account'); }}>
                  <ListItemIcon><ManageAccountsOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Moje konto" />
                </MenuItem>
              )}
              {user?.role === 'CLIENT' && (
                <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }}>
                  <ListItemIcon><ManageAccountsOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Mój profil" />
                </MenuItem>
              )}
              {user && (ACCOUNT_ROLES.includes(user.role) || user.role === 'CLIENT') && <Divider sx={{ my: 0.5 }} />}
              <MenuItem onClick={handleLogout} sx={{ color: tokens.danger }}>
                <ListItemIcon><LogoutRoundedIcon fontSize="small" sx={{ color: tokens.danger }} /></ListItemIcon>
                <ListItemText primary="Wyloguj się" />
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Tresc strony */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            minWidth: 0,
            maxWidth: isFullWidthPage ? 'none' : 1400,
            mx: 'auto',
            px: { xs: 2, sm: 3, lg: 4 },
            pt: { xs: 2.5, md: 3.5 },
            pb: { xs: 4, md: 6 },
          }}
        >
          <Outlet />
        </Box>
      </Box>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </Box>
  );
};
